import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, Clock, Trash2 } from "lucide-react";
import { getRecents, clearRecents } from "./recents";
import { useSelectRecents } from "./useSelectRecents";
import type { SelectOption } from "./types";

interface ResponsiveCommandMultiSelectListProps {
  inputRef: React.Ref<HTMLInputElement>;
  searchPlaceholder: string;
  effectiveSearch: string;
  onSearchChange: (value: string) => void;
  isSearching: boolean;
  selectedOptions: SelectOption[];
  value: string[];
  onToggleSelect: (id: string) => void;
  onClearAll: () => void;
  enableRecents: boolean;
  recentKey?: string;
  recentsNonce: number;
  options: SelectOption[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onClearRecents: () => void;
}

export function ResponsiveCommandMultiSelectList({
  inputRef,
  searchPlaceholder,
  effectiveSearch,
  onSearchChange,
  isSearching,
  selectedOptions,
  value,
  onToggleSelect,
  onClearAll,
  enableRecents,
  recentKey,
  recentsNonce,
  options,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onClearRecents,
}: ResponsiveCommandMultiSelectListProps) {
  const recentOptions = useSelectRecents({
    enableRecents,
    recentKey,
    options,
    effectiveSearch,
    recentsNonce,
  });

  const recentValueSet = React.useMemo(
    () => new Set(recentOptions.map((o) => o.value)),
    [recentOptions]
  );

  return (
    <Command shouldFilter={false} className="w-full">
      <CommandInput
        ref={inputRef}
        placeholder={searchPlaceholder}
        value={effectiveSearch}
        onValueChange={onSearchChange}
        autoFocus
      />
      <CommandList
        className="w-full max-h-[60vh] overflow-y-auto overflow-x-visible"
        onScroll={(e: React.UIEvent<HTMLDivElement>) => {
          const el = e.currentTarget as HTMLDivElement;
          const threshold = 64;
          if (
            onLoadMore &&
            hasMore &&
            !isLoadingMore &&
            el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
          ) {
            onLoadMore();
          }
        }}
      >
        <CommandEmpty>{isSearching ? "Searching..." : "No results found."}</CommandEmpty>
        {selectedOptions.length > 0 && (
          <>
            <div className="px-3 py-2 text-sm text-muted-foreground flex justify-between items-center">
              <span>{selectedOptions.length} selected</span>
              <button type="button" className="underline" onClick={onClearAll}>
                Clear all
              </button>
            </div>
            <CommandGroup heading="Selected">
              {selectedOptions.map((opt) => (
                <CommandItem
                  key={`selected-${opt.value}`}
                  value={opt.value}
                  onSelect={onToggleSelect}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1 min-w-0">{opt.node ?? opt.label}</div>
                    <Check className="ml-2 h-4 w-4 flex-shrink-0" />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {enableRecents && recentKey && recentOptions.length > 0 && (
          <CommandGroup
            className="w-full"
            heading={
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-4 w-4" /> Recent
              </div>
            }
          >
            {recentOptions
              .filter((opt) => !value.includes(opt.value))
              .map((opt) => (
                <CommandItem
                  className="w-full"
                  key={`recent-${opt.value}`}
                  value={opt.value}
                  onSelect={onToggleSelect}
                  keywords={[opt.label]}
                >
                  <div className="flex items-center w-full">
                    <div className="flex-1 min-w-0">{opt.node ?? opt.label}</div>
                  </div>
                </CommandItem>
              ))}
          </CommandGroup>
        )}
        <CommandGroup className="w-full">
          {options
            .filter((opt) => !recentValueSet.has(opt.value))
            .filter((opt) => {
              const s = (effectiveSearch || "").toLowerCase();
              if (!s) return true;
              const hay = `${opt.label} ${opt.searchableText || ""}`.toLowerCase();
              return hay.includes(s);
            })
            .map((opt) => {
            const isChecked = value.includes(opt.value);
            return (
              <CommandItem
                key={opt.value}
                value={opt.value}
                onSelect={onToggleSelect}
                keywords={[opt.label, ...(opt.searchableText?.split(" ") || [])]}
              >
                <div className="flex items-center w-full min-w-0">
                  <div className="flex-1 min-w-0 overflow-visible">
                    {opt.node ?? (
                      <span className="whitespace-nowrap">{opt.label}</span>
                    )}
                  </div>
                  {isChecked && <Check className="ml-2 h-4 w-4 flex-shrink-0" />}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>
        {onLoadMore && (
          <div className="w-full py-2 text-center text-xs text-muted-foreground">
            {isLoadingMore ? "Loading more..." : hasMore ? "Scroll for more" : "End of results"}
          </div>
        )}
        {enableRecents && recentKey && (
          <div className="flex items-center justify-end p-2">
            <Button
              type="button"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  clearRecents(recentKey);
                } catch {}
                onClearRecents();
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear recents
            </Button>
          </div>
        )}
      </CommandList>
    </Command>
  );
}
